// Le buste (épaules, encolure, col) et ce qui se porte autour du cou.
// `clipId` limite les motifs (rayures, coutures, ceinture) à la forme du vêtement.

const INK = "#2B2540";
const GOLD = "#E2B33C";
const WHITE_TEE = "#F4F1FA";
const STEEL = "#C9CED6";
const COAT = "#F7F7FB";
// Trois silhouettes : la carrure change la largeur des épaules et la ligne des trapèzes.
const TORSOS = {
    regular: "M8 126c0-20 16-28 36-30h32c20 2 36 10 36 30z",
    slim: "M17 126c0-18 13-27 29-30h28c16 3 29 12 29 30z",
    // Les trapèzes montent en pente douce jusqu'aux côtés du cou, sans palier.
    muscular: "M1 126c0-21 14-30 35-33l12-9h24l12 9c21 3 35 12 35 33z",
};
// Le cou suit : plus fin pour une silhouette élancée, plus large pour une carrure musclée.
// L'ombre du menton part de derrière le visage (bord du haut droit, caché) : aucune bande
// de peau claire ne peut apparaître entre le menton et elle.
export const NECKS = {
    regular: ["M51 80h18v14c0 5-4 8-9 8s-9-3-9-8z", "M51 81h18v9c-5 5-13 5-18 0z"],
    slim: ["M52.5 80h15v14c0 5-3.5 8-7.5 8s-7.5-3-7.5-8z", "M52.5 81h15v9c-4 5-11 5-15 0z"],
    muscular: ["M48 79h24v15c0 5-5.5 8-12 8s-12-3-12-8z", "M48 80h24v10c-6 5.5-18 5.5-24 0z"],
};

// `accent` (la couleur des accessoires) sert à la cravate du costume.
// Encolures, cols et colliers sont dessinés pour le cou normal : ils s'élargissent ou se
// resserrent avec lui, autour de l'axe du corps, pour rester raccord avec chaque silhouette.
const NECK_SCALE = { regular: 1, slim: 0.84, muscular: 1.33 };
export const neckTransform = (build, inverse = false) => {
    const scale = NECK_SCALE[build] || 1;
    return `translate(60 0) scale(${inverse ? 1 / scale : scale} 1) translate(-60 0)`;
};

// `custom` : une tenue ajoutée sans redéploiement (lib/avatar/pieces.js). Le buste est dessiné
// d'office à la carrure du membre ; le dessin de la pièce se pose dessus sans jamais en
// dépasser, et son col suit la largeur du cou.
export function Top({ style, color, skin, accent, build = "regular", clipId, custom = null }) {
    const TORSO = TORSOS[build] || TORSOS.regular;
    const clip = `url(#${clipId})`;
    const neckline = <path d="M47 95.5h26c-1 6-6 9.5-13 9.5s-12-3.5-13-9.5z" fill={skin.fill} />;
    const vee = <path d="M47.5 95.5h25L60 109z" fill={skin.fill} />;
    // Le débardeur, la robe et le haut épaules nues laissent voir la peau, la salopette se porte sur un t-shirt clair.
    const bare = ["tank", "dress", "bardot"].includes(style);
    // Blouse blanche et gilet se portent sur des manches claires : le buste est blanc, la couleur va au reste.
    const base = bare ? skin.fill : { overalls: WHITE_TEE, labcoat: COAT, waistcoat: "#FAFAFD" }[style] || color.fill;
    if (custom) {
        const { piece } = custom;
        const fill = { top: color.fill, skin: skin.fill }[piece.base || "top"] || piece.base;
        return (
            <g>
                <clipPath id={clipId}>
                    <path d={TORSO} />
                </clipPath>
                <path d={TORSO} fill={fill} />
                {custom.html && <g clipPath={clip} className={custom.className} dangerouslySetInnerHTML={custom.html} />}
                <g transform={neckTransform(build)}>
                    {(piece.neckline || "round") === "round" && neckline}
                    {piece.neckline === "vee" && vee}
                    {custom.collar && <g className={custom.className} dangerouslySetInnerHTML={custom.collar} />}
                </g>
            </g>
        );
    }
    return (
        <g>
            {style === "hoodie" && <path d="M36 99c2-12 12-17 24-17s22 5 24 17z" fill={color.shade} transform={neckTransform(build)} />}
            {/* La découpe est posée à l'envers de l'échelle du col, pour garder la forme réelle du buste. */}
            <clipPath id={clipId}>
                <path d={TORSO} transform={neckTransform(build, true)} />
            </clipPath>
            <path d={TORSO} fill={base} />
            <g transform={neckTransform(build)}>
            {style === "tee" && neckline}
            {style === "vneck" && vee}
            {style === "stripes" && (
                <g>
                    <g clipPath={clip} fill="#fff" fillOpacity="0.55">
                        <rect x="0" y="107" width="120" height="3.6" />
                        <rect x="0" y="114.5" width="120" height="3.6" />
                    </g>
                    {neckline}
                </g>
            )}
            {style === "sweater" && (
                <g>
                    {neckline}
                    <path d="M47 95.5c1 6 6 9.5 13 9.5s12-3.5 13-9.5" fill="none" stroke={color.shade} strokeWidth="3.2" strokeLinecap="round" />
                    <path
                        d="M6 115l6-5 6 5 6-5 6 5 6-5 6 5 6-5 6 5 6-5 6 5 6-5 6 5 6-5 6 5 6-5 6 5 6-5"
                        fill="none"
                        stroke="#fff"
                        strokeOpacity="0.6"
                        strokeWidth="2"
                        strokeLinejoin="round"
                        clipPath={clip}
                    />
                </g>
            )}
            {style === "polo" && (
                <g>
                    <path d="M48 95.5h24L60 108z" fill={skin.fill} />
                    <path d="M46 94.5l14 13-8 5.5-8.5-15zM74 94.5L60 107.5l8 5.5 8.5-15z" fill={color.shade} />
                    <path d="M60 108.5v9" stroke={color.shade} strokeWidth="1.6" strokeLinecap="round" />
                    <circle cx="60" cy="112" r="1" fill="#fff" fillOpacity="0.85" />
                    <circle cx="60" cy="116.5" r="1" fill="#fff" fillOpacity="0.85" />
                </g>
            )}
            {style === "tank" && (
                <g>
                    <path d="M41 126v-22c0-4 2.5-7 6-8 1.5 6.5 6 10.5 13 10.5s11.5-4 13-10.5c3.5 1 6 4 6 8v22z" fill={color.fill} />
                    <path d="M47 96c1.5 6.5 6 10.5 13 10.5s11.5-4 13-10.5" fill="none" stroke={color.shade} strokeWidth="1.6" strokeLinecap="round" />
                </g>
            )}
            {style === "dress" && (
                // Robe à fines bretelles, décolleté en cœur.
                <g>
                    <path d="M47.5 100.5L44.5 95.5M72.5 100.5L75.5 95.5" stroke={color.fill} strokeWidth="2.6" strokeLinecap="round" />
                    <path d="M40 126v-19c0-4 3-7 7-7.5 2.5-0.3 4.5 0.6 6.5 2.6s4 3 6.5 3 4.5-1 6.5-3 4-2.9 6.5-2.6c4 0.5 7 3.5 7 7.5v19z" fill={color.fill} />
                    <path d="M47 99.5c2.5-0.3 4.5 0.6 6.5 2.6s4 3 6.5 3 4.5-1 6.5-3 4-2.9 6.5-2.6" fill="none" stroke={color.shade} strokeWidth="1.4" strokeLinecap="round" />
                </g>
            )}
            {style === "overalls" && (
                <g>
                    {neckline}
                    <path d="M46.5 109L42 97M73.5 109L78 97" stroke={color.fill} strokeWidth="5" strokeLinecap="round" />
                    <path d="M43 126v-14c0-2 1.5-3.5 3.5-3.5h27c2 0 3.5 1.5 3.5 3.5v14z" fill={color.fill} />
                    <path d="M54 116h12v10H54z" fill={color.shade} opacity="0.55" />
                    <circle cx="47" cy="111" r="1.7" fill={GOLD} />
                    <circle cx="73" cy="111" r="1.7" fill={GOLD} />
                </g>
            )}
            {style === "hoodie" && (
                <g>
                    <path d="M45 96h30c-2 7-7 11-15 11s-13-4-15-11z" fill={skin.fill} />
                    <path d="M45 96c2 7 7 11 15 11s13-4 15-11" fill="none" stroke={color.shade} strokeWidth="3" strokeLinecap="round" />
                    <path d="M54 107v7M66 107v7" stroke="#fff" strokeOpacity="0.85" strokeWidth="1.8" strokeLinecap="round" />
                </g>
            )}
            {style === "turtleneck" && (
                <g>
                    <path d="M48.5 89h23v10c0 3.5-5 6-11.5 6s-11.5-2.5-11.5-6z" fill={color.fill} />
                    <path d="M48.5 96c3 3 20 3 23 0" fill="none" stroke={color.shade} strokeWidth="1.6" strokeLinecap="round" />
                </g>
            )}
            {style === "puffer" && (
                <g>
                    <path d="M46 97c0-5.5 6-8.5 14-8.5s14 3 14 8.5l-2 6c-3-3-21-3-24 0z" fill={color.shade} />
                    <path d="M10 111h100M8 119h104" stroke={color.shade} strokeWidth="1.6" clipPath={clip} />
                    <path d="M60 102v24" stroke="#fff" strokeOpacity="0.55" strokeWidth="1.4" />
                </g>
            )}
            {(style === "shirt" || style === "tie") && (
                <g>
                    <path d="M48 95.5h24L60 110z" fill={skin.fill} />
                    <path d="M46 94.5l14 15.5-9 6-9-18zM74 94.5L60 110l9 6 9-18z" fill="#fff" />
                    {style === "shirt" ? (
                        <path d="M60 110v16" stroke={color.shade} strokeWidth="1.6" />
                    ) : (
                        <path d="M57.5 109.5h5l1.2 3-1.7 2 2 11.5h-8l2-11.5-1.7-2z" fill={color.shade} />
                    )}
                </g>
            )}
            {style === "jacket" && (
                <g>
                    <path d="M46 95.5h28l-5 30.5H51z" fill="#fff" />
                    {neckline}
                    <path d="M46 95.5l8 30.5M74 95.5l-8 30.5" stroke={color.shade} strokeWidth="2.4" strokeLinecap="round" />
                </g>
            )}
            {style === "blazer" && (
                <g>
                    <path d="M48 95.5h24l-12 24z" fill="#fff" />
                    <path d="M52 95.5h16l-8 9.5z" fill={skin.fill} />
                    <path d="M46 95l14 24-6 7H43zM74 95L60 119l6 7h11z" fill={color.shade} />
                </g>
            )}
            {style === "suit" && (
                // Costume cravate : chemise blanche, col, cravate nouée, revers de veste et pochette.
                <g>
                    <path d="M48 95.5h24l-12 27z" fill="#fff" />
                    <path d="M51.5 95.5h17L60 105z" fill={skin.fill} />
                    <path d="M58.1 110.5L56 127h8l-2.1-16.5z" fill={accent.fill} />
                    <path d="M57.2 105.3h5.6l1 3.2-1.9 2.4h-3.8l-1.9-2.4z" fill={accent.shade} />
                    <path d="M47 94.5l13 11-6.8 5.8-8-15.3zM73 94.5l-13 11 6.8 5.8 8-15.3z" fill="#fff" stroke="#D9D3EA" strokeWidth="0.6" strokeLinejoin="round" />
                    <path d="M44.5 95.5L60 122l-5 6H40zM75.5 95.5L60 122l5 6h15z" fill={color.shade} />
                    <path d="M46.5 104.5l5.5 1.6M73.5 104.5l-5.5 1.6" stroke={color.fill} strokeWidth="1.2" strokeLinecap="round" />
                    <path d="M77.5 113.5l5.5-1.6 0.8 4z" fill="#fff" />
                </g>
            )}
            {style === "graphic" && (
                <g>
                    {neckline}
                    <path d="M60 109.5l1.9 3.9 4.3 0.6-3.1 3 0.7 4.3-3.8-2-3.8 2 0.7-4.3-3.1-3 4.3-0.6z" fill="#fff" fillOpacity="0.9" />
                </g>
            )}
            {style === "cardigan" && (
                <g>
                    <path d="M49.5 95.5h21l-6 30.5h-9z" fill={WHITE_TEE} />
                    {neckline}
                    <path d="M49.5 95.5l6 30.5M70.5 95.5l-6 30.5" stroke={color.shade} strokeWidth="2.8" strokeLinecap="round" />
                    <circle cx="66.8" cy="110" r="1.2" fill={color.shade} />
                    <circle cx="65.6" cy="117.5" r="1.2" fill={color.shade} />
                </g>
            )}
            {style === "denim" && (
                <g>
                    <path d="M51.5 96h17l-8.5 12.5z" fill={WHITE_TEE} />
                    <path d="M52.5 95.5h15l-7.5 8z" fill={skin.fill} />
                    <path d="M26 107l21-6M94 107l-21-6" stroke={color.shade} strokeWidth="1.3" strokeLinecap="round" clipPath={clip} />
                    <path d="M60 108.5v18" stroke={color.shade} strokeWidth="1.6" />
                    <path d="M46 94.5l14 9.5-7 6-8.5-12.5zM74 94.5l-14 9.5 7 6 8.5-12.5z" fill={color.shade} />
                    <circle cx="60" cy="113" r="1.3" fill="#D9C2A0" />
                    <circle cx="60" cy="120.5" r="1.3" fill="#D9C2A0" />
                </g>
            )}
            {style === "leather" && (
                <g>
                    <path d="M50 95.5h20l-9 19h-2z" fill={WHITE_TEE} />
                    <path d="M52.5 95.5h15l-7.5 8.5z" fill={skin.fill} />
                    <path d="M45 95l14.5 19.5-4.5 4.5-13.5-6.5zM75 95L60.5 114.5l4.5 4.5 13.5-6.5z" fill={color.shade} />
                    <path d="M61 115.5l5.5 10.5" stroke={STEEL} strokeWidth="1.6" strokeLinecap="round" />
                    <circle cx="44.5" cy="108.5" r="1" fill={STEEL} />
                    <circle cx="75.5" cy="108.5" r="1" fill={STEEL} />
                </g>
            )}
            {style === "bomber" && (
                <g>
                    {neckline}
                    <path d="M46.5 96c1.5 7 6.5 10.5 13.5 10.5s12-3.5 13.5-10.5" fill="none" stroke={color.shade} strokeWidth="4.2" strokeLinecap="round" />
                    <path d="M50.5 100.5l1.5-2M55.5 103.5l0.8-2.5M64.5 103.5l-0.8-2.5M69.5 100.5l-1.5-2" stroke={color.fill} strokeWidth="1" strokeLinecap="round" />
                    <path d="M60 107.5v19" stroke={STEEL} strokeWidth="1.6" />
                </g>
            )}
            {style === "tracksuit" && (
                <g>
                    <path d="M15 113C21 104 32 99 46 97.5M105 113C99 104 88 99 74 97.5" fill="none" stroke="#fff" strokeOpacity="0.85" strokeWidth="2.2" strokeLinecap="round" clipPath={clip} />
                    <path d="M19 117C25 108 35 103 47 101M101 117C95 108 85 103 73 101" fill="none" stroke="#fff" strokeOpacity="0.85" strokeWidth="2.2" strokeLinecap="round" clipPath={clip} />
                    <path d="M47.5 90h25v9c0 3-5.5 5.5-12.5 5.5s-12.5-2.5-12.5-5.5z" fill={color.fill} />
                    <path d="M47.5 96c3 3 22 3 25 0" fill="none" stroke={color.shade} strokeWidth="1.6" strokeLinecap="round" />
                    <path d="M60 91v35" stroke="#fff" strokeOpacity="0.85" strokeWidth="1.6" />
                </g>
            )}
            {style === "labcoat" && (
                <g>
                    <path d="M48 95.5h24l-12 25z" fill={color.fill} />
                    <path d="M52 95.5h16l-8 9.5z" fill={skin.fill} />
                    <path d="M45 95l15 25-6 6H43zM75 95L60 120l6 6h11z" fill="#E4E4EE" />
                    <rect x="74" y="112" width="9" height="9" rx="1" fill="none" stroke="#C9C9D6" strokeWidth="1" />
                    <path d="M77 109v6.5M80 110v5.5" stroke={color.fill} strokeWidth="1.6" strokeLinecap="round" />
                </g>
            )}
            {style === "waistcoat" && (
                <g>
                    <path d="M39 126v-22c2-4 6-7 9.5-8L60 119l11.5-23c3.5 1 7.5 4 9.5 8v22z" fill={color.fill} />
                    <path d="M52 95.5h16l-8 10z" fill={skin.fill} />
                    <path d="M47 94.5l13 11-6.5 5.5-8-15zM73 94.5l-13 11 6.5 5.5 8-15z" fill="#fff" stroke="#D9D3EA" strokeWidth="0.6" strokeLinejoin="round" />
                    <circle cx="60" cy="122" r="1.2" fill={color.shade} />
                </g>
            )}
            {style === "mao" && (
                <g>
                    <path d="M48.5 90.5h9.5l2 3 2-3h9.5v8.5c-3 3-7.5 4.5-11.5 4.5s-8.5-1.5-11.5-4.5z" fill={color.shade} />
                    <path d="M60 103.5v23" stroke={color.shade} strokeWidth="1.2" />
                    <path d="M55.5 109h9M55.5 115.5h9M55.5 122h9" stroke={accent.fill} strokeWidth="1.8" strokeLinecap="round" />
                </g>
            )}
            {style === "blouse" && (
                <g>
                    <path d="M53 95.5h14l-7 8z" fill={skin.fill} />
                    <path d="M46.5 95.5c0 6.5 4 10.5 9 10.5 2.5 0 4-1.2 4.5-3.5-1.5-4-6.5-7-13.5-7zM73.5 95.5c0 6.5-4 10.5-9 10.5-2.5 0-4-1.2-4.5-3.5 1.5-4 6.5-7 13.5-7z" fill="#fff" stroke="#D9D3EA" strokeWidth="0.6" />
                    <path d="M60 104v22" stroke={color.shade} strokeWidth="1.2" />
                    <circle cx="60" cy="110" r="1.1" fill="#fff" />
                    <circle cx="60" cy="117" r="1.1" fill="#fff" />
                </g>
            )}
            {style === "bardot" && (
                // Haut épaules nues : une bande froncée qui passe sous les clavicules.
                <g>
                    <path d="M12 126c1-9 6-15.5 14-18.5 10 4.5 22 6.5 34 6.5s24-2 34-6.5c8 3 13 9.5 14 18.5z" fill={color.fill} clipPath={clip} />
                    <path d="M26 107.5c10 4.5 22 6.5 34 6.5s24-2 34-6.5" fill="none" stroke={color.shade} strokeWidth="1.8" strokeLinecap="round" />
                </g>
            )}
            {style === "hawaiian" && (
                <g>
                    <g fill="#fff" fillOpacity="0.6" clipPath={clip}>
                        {[[24, 115], [33, 106], [39, 121], [50, 119], [70, 113], [77, 122], [86, 108], [94, 119]].map(([cx, cy]) => (
                            <g key={cx}>
                                <circle cx={cx - 1.7} cy={cy} r="1.5" />
                                <circle cx={cx + 1.7} cy={cy} r="1.5" />
                                <circle cx={cx} cy={cy - 1.7} r="1.5" />
                                <circle cx={cx} cy={cy + 1.7} r="1.5" />
                            </g>
                        ))}
                    </g>
                    <path d="M49 95.5h22l-11 16z" fill={skin.fill} />
                    <path d="M45.5 94.5l14.5 16-8 4.5-9.5-14zM74.5 94.5L60 110.5l8 4.5 9.5-14z" fill={color.shade} />
                    <path d="M60 111.5v15" stroke={color.shade} strokeWidth="1.4" />
                </g>
            )}
            {style === "jersey" && (
                <g>
                    {vee}
                    <path d="M47.5 95.5L60 109l12.5-13.5" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinejoin="round" strokeLinecap="round" />
                    <path d="M22 100l8 28M98 100l-8 28" stroke="#fff" strokeOpacity="0.85" strokeWidth="4" clipPath={clip} />
                </g>
            )}
            {style === "gi" && (
                <g>
                    <path d="M50 95.5h20l-10 17z" fill={skin.fill} />
                    <path d="M73 95L56 121" stroke={color.shade} strokeWidth="4.6" strokeLinecap="round" />
                    <path d="M47 95l17 26" stroke={color.shade} strokeWidth="4.6" strokeLinecap="round" />
                    <rect x="0" y="120" width="120" height="5" fill={INK} clipPath={clip} />
                </g>
            )}
            </g>
        </g>
    );
}

// Autour du cou, par-dessus le vêtement et sous le menton.
export function Neckwear({ style, color }) {
    switch (style) {
        case "scarf":
            return (
                <g>
                    <path d="M63 97l9 1.5 1 17-9 1z" fill={color.shade} />
                    <path d="M44.5 90c4.5 4 26.5 4 31 0l2 9c-6.5 5-28.5 5-35 0z" fill={color.fill} />
                    <path d="M46 95c6 3 22 3 28 0" fill="none" stroke={color.shade} strokeWidth="1.4" strokeLinecap="round" />
                </g>
            );
        case "bandana":
            return (
                <g>
                    <path d="M45.5 91.5c5 3.5 24 3.5 29 0L60 109z" fill={color.fill} />
                    <path d="M49 95.5c5 2 17 2 22 0" fill="none" stroke={color.shade} strokeWidth="1.4" strokeLinecap="round" />
                </g>
            );
        case "bowtie":
            return (
                <g>
                    <path d="M60 97.5l-8.5-5v10zM60 97.5l8.5-5v10z" fill={color.fill} />
                    <rect x="58.2" y="95.5" width="3.6" height="4" rx="1" fill={color.shade} />
                </g>
            );
        case "necklace":
            return (
                <g>
                    <path d="M50.5 95c3 6.5 16 6.5 19 0" fill="none" stroke={GOLD} strokeWidth="1.3" strokeLinecap="round" />
                    <circle cx="60" cy="100.8" r="1.9" fill={GOLD} />
                </g>
            );
        case "pearls":
            return (
                <g fill="#F7F3EC" stroke="#D9D2C4" strokeWidth="0.4">
                    {[[50.5, 95.5], [53.2, 98.2], [56.5, 99.8], [60, 100.3], [63.5, 99.8], [66.8, 98.2], [69.5, 95.5]].map(([cx, cy]) => (
                        <circle key={cx} cx={cx} cy={cy} r="1.7" />
                    ))}
                </g>
            );
        case "choker":
            return (
                <g>
                    <path d="M51 88.5c4 2.2 14 2.2 18 0" fill="none" stroke={INK} strokeWidth="2.6" strokeLinecap="round" />
                    <circle cx="60" cy="90.8" r="1.5" fill={GOLD} />
                </g>
            );
        case "lanyard":
            // Tour de cou avec badge, façon événement ou bureau.
            return (
                <g>
                    <path d="M51.5 95L59 113M68.5 95L61 113" stroke={color.fill} strokeWidth="2.2" strokeLinecap="round" />
                    <rect x="54.5" y="111" width="11" height="13" rx="2" fill="#fff" stroke={color.shade} strokeWidth="1" />
                    <rect x="57" y="114" width="6" height="2" rx="1" fill={color.shade} />
                </g>
            );
        case "chain":
            return <path d="M48.5 95c3 9.5 20 9.5 23 0" fill="none" stroke={GOLD} strokeWidth="2.3" strokeLinecap="round" strokeDasharray="0.1 3.3" />;
        case "headset":
            // Casque posé autour du cou : l'arceau passe derrière la nuque, les deux
            // écouteurs reposent sur les clavicules, coussinet tourné vers le cou.
            return (
                <g>
                    <path d="M44 93.5C43.5 88.5 46.5 85.5 51 85M76 93.5C76.5 88.5 73.5 85.5 69 85" fill="none" stroke={INK} strokeWidth="3.4" strokeLinecap="round" />
                    {[
                        { x: 37.5, pad: 44.5, turn: -10 },
                        { x: 72.5, pad: 72.5, turn: 10 },
                    ].map(({ x, pad, turn }) => (
                        <g key={x} transform={`rotate(${turn} ${x + 5} 99)`}>
                            <rect x={x} y="91.5" width="10" height="15" rx="4.6" fill={color.fill} />
                            <rect x={pad} y="92.8" width="3" height="12.4" rx="1.5" fill={INK} opacity="0.85" />
                            <path d={`M${x + (turn < 0 ? 3 : 7)} 95v8`} stroke={color.shade} strokeWidth="1.4" strokeLinecap="round" />
                        </g>
                    ))}
                </g>
            );
        default:
            return null;
    }
}
